import { Button } from "@moe/oss/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@moe/oss/ui/tabs";
import { ReloadIcon } from "@radix-ui/react-icons";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AuthModal } from "@web/components/AuthModal";
import { Navbar } from "@web/components/Navbar";
import { decode, extract } from "@web/lib/png";
import { useAppContext } from "@web/route-services/root/AppContext";
import ky from "ky";
import Mustache from "mustache";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { fromError } from "zod-validation-error";

// TODO: split this file up into route-services components
export const Route = createFileRoute("/studio/import")({
  component: Import
});

const tv2Schema = z.object({
  spec: z.literal("chara_card_v2"),
  spec_version: z.literal("2.0"),
  data: z.object({
    name: z.string().min(1, "The 'name' field is missing. This card is probably corrupted."),
    avatar: z.string().optional(),
    description: z.string(),
    personality: z.string(),
    scenario: z.string(),
    first_mes: z.string(),
    mes_example: z.string(),
    creator_notes: z.string(),
    system_prompt: z.string(),
    post_history_instructions: z.string(),
    alternate_greetings: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
    creator: z.string().optional()
  })
});
type TV2 = z.infer<typeof tv2Schema>;

const tv1Schema = z.object({
  name: z.string(),
  description: z.string(),
  personality: z.string().optional(),
  scenario: z.string().optional(),
  first_mes: z.string(),
  mes_example: z.string().optional()
});
type TV1 = z.infer<typeof tv1Schema>;

const descriptionTemplate = `
{{#data.description}}
<character_description>
{{{.}}}
</character_description>
{{/data.description}}
  
{{#data.personality}}
<character_personality>
{{{.}}}
</character_personality>
{{/data.personality}}

{{#data.scenario}}
<scenario>
{{{.}}}
</scenario>
{{/data.scenario}}

{{#data.system_prompt}}
<system_prompt>
{{{.}}}
</system_prompt>
{{/data.system_prompt}}
`.trim();

async function downloadImageFile(url: string): Promise<File> {
  const type = url.split(".").pop() || "png";
  const response = await fetch(url);
  const blob = await response.blob();
  return new File([blob], `image.${type}`, { type: `image/${type}` });
}

async function png2JSON(file: File) {
  const uint8 = new Uint8Array(await file.arrayBuffer());
  const texts = extract(uint8)
    .filter((chunk) => chunk.name === "tEXt")
    .map((chunk) => decode(chunk.data));

  const base64 = texts.find((text) => text.keyword.toLowerCase() === "chara")?.text;
  if (!base64) {
    throw new Error("This is either a non tavern v2 card, or the file is corrupted.");
  }
  return JSON.parse(atob(base64));
}

async function importTV1(parsed: TV1, file: File, access: any) {
  if (!file) throw new Error("No file selected");
  const type = file.type;

  // V1 must be a PNG; if user tries with JSON => error
  if (type === "application/json") {
    throw new Error("Tavern V1 card must be a PNG.");
  }

  const avatar: File | undefined = file;
  const description = Mustache.render(descriptionTemplate, { data: parsed }).trim();

  await access.character.create({
    title: parsed.name,
    tagline: "",
    name: parsed.name,
    description: description,
    greeting: parsed.first_mes,
    messageExamples: parsed.mes_example,
    avatar,
    externalKind: "tavern",
    isNSFW: true,
    isPrivate: true
  });
}

/** Import a Tavern V2 card. */
async function importTV2(parsed: TV2, file: File, access: any) {
  if (!file) throw new Error("No file selected");
  const type = file.type;
  let avatar: File | undefined = file;
  if (type === "application/json") {
    if (!parsed.data.avatar) {
      throw new Error("This tavern JSON file does not have an avatar.");
    }
    avatar = await downloadImageFile(parsed.data.avatar);
  }

  const externalCreatedBy = parsed.data.creator === "" ? undefined : parsed.data.creator;
  const description = Mustache.render(descriptionTemplate, { data: parsed.data }).trim();

  await access.character.create({
    title: parsed.data.name,
    tagline: parsed.data.creator_notes,
    name: parsed.data.name,
    description: description,
    greeting: parsed.data.first_mes,
    messageExamples: parsed.data.mes_example,
    alternativeGreetings: parsed.data.alternate_greetings,
    avatar,
    externalCreatedBy,
    externalKind: "tavern",
    tags: parsed.data.tags,
    isNSFW: true,
    isPrivate: true
  });
}

/**
 * Given raw JSON data, attempts to parse it as Tavern v1 or v2,
 * and calls the relevant import function.
 */
async function importTavernData(json: any, file: File, access: any) {
  const tv1Res = tv1Schema.safeParse(json);
  const tv2Res = tv2Schema.safeParse(json);

  if (tv2Res.success) {
    await importTV2(tv2Res.data, file, access);
    return;
  }
  if (tv1Res.success) {
    await importTV1(tv1Res.data, file, access);
    return;
  }

  throw new Error(
    `This file is neither a Tavern V1 nor V2 Card. 
     V1 Error: ${fromError(tv1Res.error)}. 
     V2 Error: ${fromError(tv2Res.error)}`
  );
}

function Import() {
  return (
    <div className="flex">
      <Navbar />
      <Tabs defaultValue="file" className="text-tx-primary mx-auto mt-4 w-full px-4 font-medium md:mt-56 md:w-[30rem]">
        <TabsList className="grid grid-cols-2">
          <TabsTrigger className="font-semibold" value="file">
            File
          </TabsTrigger>
          <TabsTrigger className="font-semibold" value="link">
            Link
          </TabsTrigger>
        </TabsList>
        <TabsContent value="file" className="">
          <FileImport />
        </TabsContent>
        <TabsContent value="link">
          <LinkImport />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FileImport() {
  const [file, setFile] = useState<File>();
  const { access, session, createModal } = useAppContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const loadingToastRef = useRef<string | number>();

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!file) {
        throw new Error("No file selected");
      }
      const type = file.type;
      if (type !== "image/png" && type !== "application/json") {
        throw new Error("Only .png and .json files are supported");
      }
      // If it's a PNG, extract JSON from tEXt chunk; if it's JSON, parse it directly.
      const json: any = type === "application/json" ? JSON.parse(await file.text()) : await png2JSON(file);

      await importTavernData(json, file, access);
    },
    onMutate: () => {
      loadingToastRef.current = toast.loading("Importing... This should take less than 10 seconds... ");
    },
    onSuccess: () => {
      if (loadingToastRef.current) toast.dismiss(loadingToastRef.current);
      toast("Imported! Check your profile page!");
      if (inputRef.current) inputRef.current.value = "";
    },
    onError: (error: any) => {
      if (loadingToastRef.current) toast.dismiss(loadingToastRef.current);
      if (error instanceof z.ZodError) {
        const errorMessage = fromError(error).message;
        toast.error(errorMessage);
        return;
      }
      toast.error(error.message);
    }
  });

  const handleImport = () => {
    if (!session) {
      createModal({ content: <AuthModal formKind="signup" /> });
      return;
    }
    importMutation.mutate();
  };

  return (
    <div className="bg-container-primary flex flex-col rounded-lg p-6 shadow-md">
      <h3 className="text-tx-primary mb-4 text-xl font-semibold">Import A Tavern JSON or PNG file</h3>
      <div className="mb-12 w-full">
        <input
          ref={inputRef}
          type="file"
          accept="application/json,image/png"
          capture={false}
          className="text-tx-primary bg-input-primary hover:bg-input-secondary
              file:bg-action-tertiary file:text-tx-primary 
              block h-10
              w-full cursor-pointer
              rounded-lg transition duration-200 ease-in-out file:mr-4 
              file:h-10 file:cursor-pointer file:rounded-l-lg file:border-0 
              file:px-4 file:py-2 file:text-sm file:font-medium
              file:transition-all hover:file:brightness-110 focus:outline-none 
              "
          onChange={(e) => {
            const file = e.target.files?.[0];
            setFile(file);
          }}
        />
      </div>

      <Button
        disabled={!file || importMutation.isPending}
        onClick={handleImport}
        className="bg-action-primary text-tx-primary mx-auto w-32
            font-medium shadow-sm"
      >
        {importMutation.isPending && <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />}
        Import
      </Button>
    </div>
  );
}

function LinkImport() {
  /* Once more sites are added for support, move these domain checks + fetching into a new file or helper if needed. */
  const supportedDomains = [new URL("https://www.chub.ai/"), new URL("https://www.characterhub.org")];

  const { access, session, createModal } = useAppContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const validateURL = (url: URL): boolean => {
    return supportedDomains.some((domain) => domain.hostname === url.hostname);
  };

  const fetchChub = async (url: URL) => {
    try {
      const endpoint = new URL("/api" + url.pathname, "https://api.chub.ai");
      const getID: any = await ky.get(endpoint).json();
      const id = getID?.node?.id;

      const card = await ky
        .get("https://api.chub.ai/api/v4/projects/" + id + "/repository/files/raw%252Ftavern_raw.json/raw")
        .json();
      return card;
    } catch (error) {
      throw new Error(`Could not fetch from chub.ai: ${String(error)}`);
    }
  };

  const handleImportLink = async () => {
    if (!session) {
      createModal({ content: <AuthModal formKind="signup" /> });
      return;
    }

    if (!inputRef.current?.value) {
      toast.error("Don't put in something blank! >:(");
      return;
    }

    try {
      const url = new URL(inputRef.current.value);

      if (!validateURL(url)) {
        toast.error("URL not supported. Please use chub.ai or characterhub.org");
        return;
      }

      let card: any;
      if (url.hostname === "www.chub.ai" || url.hostname === "www.characterhub.org") {
        card = await fetchChub(url);
      }

      //shhhhh we'll fix it in post
      const dummyFile = new File([""], "linkImport.json", {
        type: "application/json"
      });

      await importTavernData(card, dummyFile, access);

      toast("Imported from link! Check your profile page!");
      if (inputRef.current) inputRef.current.value = "";
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast.error(fromError(error).message);
      } else {
        toast.error(error.message);
      }
    }
  };

  return (
    <div className="bg-container-primary flex flex-col rounded-lg p-6 shadow-md">
      <h3 className="text-tx-primary text-xl font-semibold">Import JSON from Link</h3>
      <h4 className="text-tx-secondary mb-4 text-sm font-semibold">Currently supported: chub.ai, characterhub.org</h4>
      <div className="mb-12 w-full">
        <input
          ref={inputRef}
          type="url"
          placeholder="e.g. https://chub.ai/characters/Uwhm/imogen-892c2413a563"
          className="text-tx-primary bg-input-primary hover:bg-input-secondary
              block h-10
              w-full rounded-lg px-4
              transition duration-200 ease-in-out focus:outline-none"
          pattern="https?://.+"
          title="Please enter a valid URL (starting with http:// or https://)"
        />
      </div>

      <Button
        className="bg-action-primary text-tx-primary mx-auto w-32
            font-medium shadow-sm"
        onClick={handleImportLink}
      >
        Import
      </Button>
    </div>
  );
}
